"use client";

import React from "react";

const inputBaseStyles =
  "w-full px-3 py-1.5 text-base text-darkBlack bg-white border border-lightGrey3 rounded focus:outline-none focus:ring-2 focus:ring-lightCyan focus:border-lightCyan placeholder:text-darkBlack/60 disabled:bg-lightGrey disabled:cursor-default transition-colors";

const textareaBaseStyles =
  "w-full px-3 py-1.5 text-base text-darkBlack bg-white border border-lightGrey3 rounded focus:outline-none focus:ring-2 focus:ring-lightCyan focus:border-lightCyan placeholder:text-darkBlack/60 resize-y min-h-[85px] transition-colors";

/**
 * Form.Group - Wraps a label + control.
 * Use as={Row} or horizontal for label-left, input-right layout.
 * Default is vertical (label on top).
 */
function FormGroup({ children, className = "", as, horizontal, controlId: _controlId, ...props }) {
  // controlId: react-bootstrap API only — omit from DOM
  const isHorizontal = horizontal || (as && typeof as === "function");
  const layoutClass = isHorizontal
    ? "flex flex-row flex-nowrap items-center gap-x-3 gap-y-1 mb-5"
    : "flex flex-col gap-1";
  return (
    <div className={`${layoutClass} ${className}`.trim()} {...props}>
      {children}
    </div>
  );
}

/** Fixed width for label column in horizontal forms - keeps labels compact, inputs aligned */
const LABEL_COLUMN_WIDTH = "120px";

/**
 * Form.Label - Label for form controls
 * When column and sm are set, used for horizontal layout (label left of input).
 * Uses fixed width so inputs align vertically without large gaps.
 */
function FormLabel({ children, htmlFor, className = "", column, sm, style, ...props }) {
  const layoutClass = column ? "block shrink-0" : "";
  const smWidth = column && sm
    ? { width: LABEL_COLUMN_WIDTH, minWidth: LABEL_COLUMN_WIDTH, flex: `0 0 ${LABEL_COLUMN_WIDTH}` }
    : {};
  return (
    <label
      htmlFor={htmlFor}
      className={`text-darkBlack font-medium ${layoutClass} ${className}`.trim()}
      style={{ ...smWidth, ...style }}
      {...props}
    >
      {children}
    </label>
  );
}

const selectBaseStyles =
  "w-full px-3 py-1.5 text-base text-darkBlack bg-white border border-lightGrey3 rounded focus:outline-none focus:ring-2 focus:ring-lightCyan focus:border-lightCyan disabled:bg-lightGrey disabled:cursor-default transition-colors";

const validationClasses = (isInvalid, isValid) => {
  if (isInvalid) {
    return "border-red-500 focus:border-red-500 focus:ring-red-500/35";
  }
  if (isValid) {
    return "border-green-600 focus:border-green-600 focus:ring-green-600/35";
  }
  return "";
};

/**
 * Bootstrap-compatible validation hint below Form.Control.
 * Prefer named import where `Form.Control.Feedback` is undefined (bundler/re-export edge cases).
 */
export function FormControlFeedback({ type = "valid", children, className = "", ...props }) {
  const text =
    children == null || typeof children === "boolean"
      ? ""
      : String(children).trim();
  if (!text) return null;
  return (
    <div
      className={`mt-0.5 w-full text-sm ${type === "invalid" ? "text-red-600" : "text-green-700"} ${className}`.trim()}
      {...props}
    >
      {children}
    </div>
  );
}

/**
 * Form.Control - Input, textarea, or select with MKG styling.
 * Use as="textarea" for textarea, as="select" for select, otherwise renders input.
 */
export function FormControl({
  as,
  type = "text",
  rows,
  className = "",
  children,
  isInvalid,
  isValid,
  ...props
}) {
  const stateClass = validationClasses(!!isInvalid, !!isValid && !isInvalid);
  const ariaInvalid = isInvalid ? true : undefined;

  if (as === "textarea") {
    const controlClassName = `${textareaBaseStyles} ${stateClass} ${className}`.trim();
    return (
      <textarea
        rows={rows ?? 3}
        className={controlClassName}
        aria-invalid={ariaInvalid}
        {...props}
      />
    );
  }

  if (as === "select") {
    const controlClassName = `${selectBaseStyles} h-9 ${stateClass} ${className}`.trim();
    return (
      <select
        className={controlClassName}
        aria-invalid={ariaInvalid}
        {...props}
      >
        {children}
      </select>
    );
  }

  const controlClassName = `${inputBaseStyles} h-9 ${stateClass} ${className}`.trim();
  return (
    <input
      type={type}
      className={controlClassName}
      aria-invalid={ariaInvalid}
      {...props}
    />
  );
}

FormControl.Feedback = FormControlFeedback;

/**
 * Form - Container for form components.
 * Provides Form.Group, Form.Label, Form.Control.
 */
export function Form({ children, onSubmit, className = "", ...props }) {
  if (onSubmit) {
    return (
      <form onSubmit={onSubmit} className={className} {...props}>
        {children}
      </form>
    );
  }
  return <div className={className} {...props}>{children}</div>;
}

/**
 * Form.Check - Checkbox or switch.
 * type="switch" for toggle, type="checkbox" for checkbox.
 */
function FormCheck({
  type = "checkbox",
  id,
  checked,
  onChange,
  className = "",
  label,
  children,
  ...props
}) {
  const isSwitch = type === "switch";
  const { style: inputStyle, ...inputProps } = props;
  const checkmarkSvg = "url(\"data:image/svg+xml,%3Csvg viewBox='0 0 16 16' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M3.2 8.3l3.1 3.1 6.4-7.1' stroke='white' stroke-width='2.4' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E\")";
  const input = (
    <input
      type="checkbox"
      id={id}
      checked={checked}
      onChange={onChange}
      role={isSwitch ? "switch" : "checkbox"}
      className={isSwitch
        ? "sr-only peer"
        : "h-4 w-4 shrink-0 appearance-none rounded-[0.15em] border bg-center bg-no-repeat focus:ring-1 focus:ring-lightCyan/40"}
      style={!isSwitch ? {
        borderColor: checked ? "#3490dc" : "#dee2e6",
        backgroundColor: checked ? "#3490dc" : "#ffffff",
        backgroundImage: checked ? checkmarkSvg : "none",
        backgroundSize: "0.72em 0.72em",
        ...inputStyle,
      } : inputStyle}
      {...inputProps}
    />
  );

  if (isSwitch) {
    return (
      <label
        htmlFor={id}
        className={`relative inline-flex h-6 w-11 shrink-0 cursor-pointer items-center rounded-full bg-lightGrey2 transition-colors peer-focus:ring-2 peer-focus:ring-lightCyan peer-focus:ring-offset-2 peer-checked:bg-lightCyan peer-disabled:cursor-not-allowed peer-disabled:opacity-50 ${className}`.trim()}
      >
        {input}
        <span className="pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition-transform peer-checked:translate-x-6" />
      </label>
    );
  }

  return (
    <label htmlFor={id} className={`flex w-full items-center gap-2 cursor-pointer py-0.5 ${className}`.trim()}>
      {input}
      {(label || children) && <span className="flex-1 min-w-0">{label || children}</span>}
    </label>
  );
}

/**
 * Form.Select - Select dropdown input.
 */
function FormSelect({ children, className = "", size, ...props }) {
  const sizeClasses = {
    sm: "h-8 text-sm",
    lg: "h-12 text-lg",
  };

  return (
    <select
      className={`w-full px-3 py-1.5 text-base text-darkBlack bg-white border border-lightGrey3 rounded focus:outline-none focus:ring-2 focus:ring-lightCyan focus:border-lightCyan disabled:bg-lightGrey disabled:cursor-default transition-colors ${sizeClasses[size] || "h-9"} ${className}`.trim()}
      {...props}
    >
      {children}
    </select>
  );
}

Form.Group = FormGroup;
Form.Label = FormLabel;
Form.Control = FormControl;
Form.Control.Feedback = FormControlFeedback;
Form.Check = FormCheck;
Form.Select = FormSelect;
